import { Box, Button, IconButton, Switch, Text } from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { RiDeleteBinLine, RiFileEditLine, RiCheckFill } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import CTable from "../../../components/Table/table";
import { brandPalette } from "../../../constants/brandPalette";
import {
  deleteExamRequest,
  enableExamRequest,
  publishedExamRequest,
  enableRedoRequest,
  publishExamRequest,
  restrictExamRequest,
} from "../../../modules/admin-modules/exams/Actions";
import { openCreateExamDataModel } from "../../../modules/admin-modules/models/Actions";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";

const C = {
  primary: brandPalette.primary,
  secondary: brandPalette.accentDark,
  border: brandPalette.stone,
};

const iconBtnProps = {
  variant: "ghost",
  border: "0",
  size: "sm",
  color: C.primary,
  _hover: { bg: "rgba(91, 13, 22, 0.08)", color: C.secondary },
};

const ExamsTable = ({ setAdd, setSelectedData, searchTerm = "" }) => {
  const exams = useSelector((state) => state.exams.exams);
  const user = useSelector((state) => state.user.userData);
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.exams.isLoading);
  const publishLoading = useSelector((state) => state.exams.publishLoading);
  const restrictLoading = useSelector((state) => state.exams.restrictLoading);
  const [userRole, setUserRole] = useState("");

  const [offset, setPage] = useState(0);
  const [size, setPerPage] = useState(10);
  const [pageNumber, setPageNumber] = useState(0);
  const [selected, setSelected] = useState({ id: "" });
  const { confirmDelete, deleteConfirmModal } = useDeleteConfirm({
    title: "Delete exam?",
  });

  useEffect(() => {
    if (user?.role?.length > 0) setUserRole(user?.role[0]?.name);
  }, [user?.role]);

  const list = useMemo(() => {
    const raw = Array.isArray(exams) ? exams : [];
    const q = (searchTerm || "").trim().toLowerCase();
    if (!q) return raw;
    return raw.filter(
      (e) =>
        e?.name?.toLowerCase().includes(q) ||
        e?.subject?.name_ar?.toLowerCase().includes(q)
    );
  }, [exams, searchTerm]);

  useEffect(() => {
    setPageNumber(0);
  }, [searchTerm]);

  const viewData = (row) => {
    const plain = list.find((e) => e.id === row?.id) ?? row;
    setSelectedData(plain);
    setSelected(plain);
  };

  const Data = list.map((data) => ({
    ...data,
    id: data?.id,
    title: data?.name,
    publish: (
      <Button
        size="sm"
        borderRadius="full"
        fontWeight="800"
        onClick={(e) => {
          e.stopPropagation();
          setSelectedData(data);
          dispatch(publishExamRequest(data.id));
        }}
        isLoading={data?.id === selected?.id ? publishLoading : false}
        leftIcon={data?.auto_publish_result === 0 ? null : <RiCheckFill />}
        bg={data?.auto_publish_result === 0 ? C.primary : "#ddffc2"}
        color={data?.auto_publish_result === 0 ? "white" : "black"}
      >
        {data?.auto_publish_result === 0 ? "Publish marks" : "Published"}
      </Button>
    ),
    enable: (
      <Box onClick={(e) => e.stopPropagation()}>
        <Switch
          size="md"
          colorScheme="green"
          defaultChecked={data?.enable === 0}
          onChange={() => dispatch(enableExamRequest(data?.id))}
        />
      </Box>
    ),
    redo: (
      <Box onClick={(e) => e.stopPropagation()}>
        <Switch
          size="md"
          colorScheme="orange"
          defaultChecked={data?.redo_flag === 1}
          onChange={() => dispatch(enableRedoRequest(data?.id))}
        />
      </Box>
    ),
    publish_exam: (
      <Box onClick={(e) => e.stopPropagation()}>
        <Switch
          size="md"
          colorScheme="purple"
          defaultChecked={data?.published === 0}
          onChange={() => dispatch(publishedExamRequest(data?.id))}
        />
      </Box>
    ),
    restrict:
      userRole !== "Teacher" ? (
        <Button
          size="sm"
          borderRadius="full"
          bg={C.primary}
          color="white"
          fontWeight="800"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedData(data);
            dispatch(restrictExamRequest(data.id));
          }}
          isLoading={data?.id === selected?.id ? restrictLoading : false}
        >
          Restrict absent
        </Button>
      ) : (
        <Text color="gray.400">—</Text>
      ),
    edit: (
      <IconButton
        {...iconBtnProps}
        aria-label="Edit exam"
        fontSize="20px"
        icon={<RiFileEditLine />}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedData(data);
          setAdd(false);
          dispatch(openCreateExamDataModel());
        }}
      />
    ),
    delete: (
      <IconButton
        {...iconBtnProps}
        aria-label="Delete exam"
        fontSize="20px"
        icon={<RiDeleteBinLine color="#9b2c2c" />}
        onClick={(e) => {
          e.stopPropagation();
          confirmDelete({
            name: data?.title || data?.name,
            onConfirm: () => dispatch(deleteExamRequest(data?.id)),
          });
        }}
      />
    ),
  }));

  const baseColumns = [
    { Header: "Exam", accessor: "title" },
    { Header: "Subject", accessor: "subject.name_ar" },
    { Header: "Publish answers", accessor: "publish" },
    { Header: "Exam enabled", accessor: "enable" },
    { Header: "Allow retake", accessor: "redo" },
    { Header: "Exam published", accessor: "publish_exam" },
  ];

  const columns =
    userRole !== "Teacher"
      ? [...baseColumns, { Header: "Absent restriction", accessor: "restrict" }, { Header: "Edit", accessor: "edit" }]
      : [...baseColumns, { Header: "Edit", accessor: "edit" }];

  const totalPage = Math.max(1, Math.ceil(list.length / size));

  return (
    <>
    <Box px={{ base: 4, md: 8 }} pb={10} w="100%">
      <Box
        bg="var(--surface-raised)"
        border="1px solid"
        borderColor={C.border}
        borderRadius="16px"
        overflow="hidden"
        boxShadow="sm"
      >
        <CTable
          selectedData={viewData}
          Columns={columns}
          Data={Data}
          path="openModel"
          Title=""
          subTitle=""
          btnTitle=""
          placeHolder=""
          noSearchBar
          noFilter
          footerBtnTitle
          filterList
          addButton
          addButtonTitle=""
          setPage={setPage}
          setPerPage={setPerPage}
          currentpage={pageNumber}
          setPageNumber={setPageNumber}
          noSecondSearchBar
          isLoading={isLoading}
          perPage={size}
          totalPage={totalPage}
          wrapperProps={{ maxH: "unset", overflow: "auto" }}
          tableProps={{ bg: "transparent", borderRadius: "0", mb: 0 }}
          theadProps={{ bg: "var(--surface-muted)", color: "var(--text-primary)" }}
        />
      </Box>
    </Box>
    {deleteConfirmModal}
    </>
  );
};

export default ExamsTable;
